import { FC, useEffect } from "react";
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDialog,
  FormDialogCancel,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
  toast,
} from "@capacit-com/lara-law-shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Dashboard } from "@/models/dashboard/Dashboard";
import { useCreateDashboard } from "@/hooks/useDashboard";
import useTenant from "@/hooks/useTenant";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";
import { Permission } from "@/lib/permissions/Permission.enum";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";
import useDashboardStore from "@/stores/dashboard.store";
import { findOrganisationNameClash } from "../lib/dashboardNameClash";
import DashboardAudienceChoice from "./DashboardAudienceChoice";
import DashboardNameClashWarning, {
  NAME_CLASH_INPUT_CLASS,
} from "./DashboardNameClashWarning";

const formSchema = z.object({
  name: z.string().trim().min(1, { message: "Udfyld navn" }),
  isOrganizationDashboard: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

interface NewDashboardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboards: Dashboard[];
  /** "Opret organisationsdashboard" opens the dialog with Organisation already chosen. */
  initialIsOrganizationDashboard: boolean;
}

const NewDashboardDialog: FC<NewDashboardDialogProps> = ({
  open,
  onOpenChange,
  dashboards,
  initialIsOrganizationDashboard,
}) => {
  const createDashboard = useCreateDashboard();
  const hasPermission = useHasPermission();
  const { setSelectedDashboard, setLocalConfig } = useDashboardStore();
  const { useQueryCurrent } = useTenant();
  const { data: currentTenant } = useQueryCurrent();

  const canCreateOrgDashboard = hasPermission({
    allOf: [Permission.UpdateDashboardLayout],
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: "", isOrganizationDashboard: false },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      name: "",
      isOrganizationDashboard:
        canCreateOrgDashboard && initialIsOrganizationDashboard,
    });
  }, [open, initialIsOrganizationDashboard, canCreateOrgDashboard, form]);

  const name = form.watch("name");
  const isOrganizationDashboard = form.watch("isOrganizationDashboard");
  const nameClash = findOrganisationNameClash(name, dashboards);
  const isPending = createDashboard.isPending;

  const handleCreate = form.handleSubmit(async (values) => {
    const asOrganization =
      canCreateOrgDashboard && values.isOrganizationDashboard;
    try {
      const created = await createDashboard.mutateAsync({
        name: values.name,
        configJson: JSON.stringify({ widgets: [] }),
        isOrganizationDashboard: asOrganization,
      });
      setSelectedDashboard(created.id);
      setLocalConfig({ widgets: [] });
      onOpenChange(false);
      // Organisation dashboards start as drafts that only administrators see (LAR-93).
      toast.success(
        asOrganization
          ? "Organisationsdashboard oprettet"
          : "Dashboard oprettet",
        {
          description: asOrganization
            ? `"${created.name}" er oprettet som kladde. Publicér det, når kollegerne skal kunne se det.`
            : `Dashboard "${created.name}" er blevet oprettet.`,
        }
      );
    } catch (e) {
      logger.error("Error creating dashboard", {
        operation: "createDashboard",
        error: e,
        isOrganizationDashboard: asOrganization,
      });
      toast.error("Fejl", {
        description: "Der opstod en fejl ved oprettelse af dashboard.",
      });
    }
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && isPending) return;
        onOpenChange(next);
      }}
      isDirty={form.formState.isDirty}
      id="new-dashboard-dialog"
    >
      <DialogHeader>
        <DialogTitle>Nyt dashboard</DialogTitle>
        <DialogDescription>
          {canCreateOrgDashboard
            ? "Vælg, hvem dashboardet er til, og giv det et navn. Du kan tilføje widgets bagefter."
            : "Giv dit nye dashboard et navn. Du kan tilføje widgets bagefter."}
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={handleCreate}>
          <div className="flex flex-col gap-4 py-4">
            {canCreateOrgDashboard && (
              <DashboardAudienceChoice
                idPrefix="new-dashboard"
                isOrganizationDashboard={isOrganizationDashboard}
                onChange={(value) =>
                  form.setValue("isOrganizationDashboard", value, {
                    shouldDirty: true,
                  })
                }
                tenantName={currentTenant?.name}
                disabled={isPending}
              />
            )}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Navn</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={
                        isOrganizationDashboard
                          ? "Organisationsdashboard"
                          : "Mit dashboard"
                      }
                      disabled={isPending}
                      autoFocus
                      className={cn(nameClash && NAME_CLASH_INPUT_CLASS)}
                    />
                  </FormControl>
                  <FormMessage />
                  <DashboardNameClashWarning
                    clash={nameClash}
                    isOrganizationDashboard={isOrganizationDashboard}
                  />
                </FormItem>
              )}
            />
          </div>

          <DialogFooter>
            <FormDialogCancel disabled={isPending} />
            <Button
              type="submit"
              id="create-dashboard-submit"
              disabled={!name.trim() || isPending}
            >
              {isPending ? (
                <>
                  <Spinner className="h-4 w-4 mr-2" />
                  Opretter...
                </>
              ) : nameClash ? (
                "Opret alligevel"
              ) : (
                "Opret"
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default NewDashboardDialog;
